import { ComputeQuoteInput, QuoteResult, ComputedSection, ResolvedPart, RampShape } from './types';
import { optimizeTreadSections } from './treadSections';
import { computePlatforms } from './platforms';
import { computeInstallation, computeUninstallation } from './installation';
import { computeTransport } from './transport';
import { computeRental } from './rental';
import { computeTaxes } from './taxes';
import { Product, Catalog } from '@gorampe/catalog-adapter';
import { validate } from '@gorampe/rules-engine';

// ---------------------------------------------------------------------------
// Geometry helpers (Phase A enrichment — totals)
// ---------------------------------------------------------------------------

/**
 * Total inclined horizontal run in inches (sum of arms only, EXCLUDING
 * horizontal landings — used for slope/length calculation). For U180 we
 * exclude landingDepthIn because the landing is flat (no slope on it).
 */
function computeInclinedRunIn(shape: RampShape): number {
  switch (shape.kind) {
    case 'straight':
      return shape.totalRunIn;
    case 'L90':
      return shape.arm1RunIn + shape.arm2RunIn;
    case 'U180':
      return shape.arm1RunIn + shape.arm2RunIn;
    case 'switchback':
      return shape.segments.reduce((sum, s) => sum + s.runIn, 0);
    case 'freeform':
      return 0;
    default:
      return 0;
  }
}

function formatShapeLabel(shape: RampShape): string {
  switch (shape.kind) {
    case 'straight':
      return 'Droite';
    case 'L90':
      return `L à 90° ${shape.turnDirection === 'left' ? 'gauche' : 'droite'}`;
    case 'U180':
      return 'U à 180°';
    case 'switchback':
      return `Switchback (${shape.segments.length} sections)`;
    case 'freeform':
      return 'Forme libre';
    default:
      return 'Inconnu';
  }
}

// ---------------------------------------------------------------------------
// Product lookup helpers (heuristic — Phase 1 calibration, Phase 2 to refine)
// ---------------------------------------------------------------------------

/**
 * Find platform product. GoRampe platforms are always 48"x48" — regardless of
 * ramp widthIn. The floor variant (plein/mailles) is taken from config.
 */
function findPlatformProduct(catalog: Catalog, floor: 'plein' | 'mailles'): Product | undefined {
  for (const p of catalog.products.values()) {
    if (p.category !== 'platform') continue;
    if (!p.availableFloors.includes(floor)) continue;
    // 48" x 48" head platform
    if (p.descriptionFr.includes('48" X 48"') || p.descriptionFr.includes('48\\" X 48\\"'))
      return p;
  }
  return undefined;
}

/**
 * Find tread product by exact length and floor.
 * Catalog description pattern: `TROTTOIR 36" X <lengthIn>", PLANCHER <FLOOR>`
 * Only standard 36"-wide residential treads (F02013–F02016) have real prices;
 * XL-GO 48 3/8" are $0 right now.
 * We match `TROTTOIR 36" X <len>"` (not XL-GO, not 48 3/8") first.
 */
function findTreadProduct(
  catalog: Catalog,
  lengthIn: number,
  floor: 'plein' | 'mailles',
): Product | undefined {
  const lenStr = String(lengthIn);

  // Pass 1: prefer standard TROTTOIR 36" products (not XL-GO)
  for (const p of catalog.products.values()) {
    if (p.category !== 'tread') continue;
    if (!p.availableFloors.includes(floor)) continue;
    if (p.descriptionFr.includes('XL-GO')) continue;
    // Pattern: `TROTTOIR 36" X <len>"`
    if (p.descriptionFr.includes(`36" X ${lenStr}"`)) return p;
  }

  // Pass 2: fallback — any tread matching the length (may be $0)
  for (const p of catalog.products.values()) {
    if (p.category !== 'tread') continue;
    if (!p.availableFloors.includes(floor)) continue;
    if (p.descriptionFr.includes(`X ${lenStr}"`)) return p;
  }

  return undefined;
}

/**
 * Find rail product for tread sections.
 * Catalog patterns:
 *  - ajouré trottoir:  `GARDE-CORPS AJOURÉ <len>", TROTTOIR`
 *  - barrotin trottoir: `GARDE-CORPS BARROTINS TROTTOIR <len>" DROIT` (matched first, price likely $0)
 * Returns first matching product (may have $0 price if not yet priced in catalog).
 */
function findRailProduct(
  catalog: Catalog,
  railType: string,
  lengthIn: number,
): Product | undefined {
  if (railType === 'aucun') return undefined;
  const lenStr = String(lengthIn);

  for (const p of catalog.products.values()) {
    if (p.category !== 'rail') continue;
    const desc = p.descriptionFr.toUpperCase();

    if (railType === 'ajoure') {
      // GARDE-CORPS AJOURÉ <len>", TROTTOIR (no BARROTINS, no BOUCLE)
      if (!desc.includes('AJOUR')) continue;
      if (desc.includes('BARROTIN') || desc.includes('BOUCLE') || desc.includes('PLATEFORME'))
        continue;
      if (desc.includes(`${lenStr}"`) && desc.includes('TROTTOIR')) return p;
    } else if (railType === 'barrotin' || railType === 'barrotin_mains') {
      // GARDE-CORPS BARROTINS TROTTOIR <len>" DROIT|GAUCHE
      if (!desc.includes('BARROTIN')) continue;
      if (!desc.includes('TROTTOIR')) continue;
      if (desc.includes(`${lenStr}"`)) {
        // Prefer DROIT over GAUCHE for the representative product
        if (desc.includes('DROIT')) return p;
      }
    }
  }

  // Fallback: try again without DROIT preference (for barrotin — pick any)
  if (railType === 'barrotin' || railType === 'barrotin_mains') {
    for (const p of catalog.products.values()) {
      if (p.category !== 'rail') continue;
      const desc = p.descriptionFr.toUpperCase();
      if (!desc.includes('BARROTIN') || !desc.includes('TROTTOIR')) continue;
      if (desc.includes(`${lenStr}"`)) return p;
    }
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// Main orchestrator
// ---------------------------------------------------------------------------

export function computeQuote({ submission, catalog }: ComputeQuoteInput): QuoteResult {
  const sections: ComputedSection[] = [];
  const partsList: ResolvedPart[] = [];
  const warnings: QuoteResult['warnings'] = [];
  const errors: QuoteResult['errors'] = [];

  // ------------------------------------------------------------------
  // 1. Platforms (head + turn landings + rest landings if needed)
  // ------------------------------------------------------------------
  const platforms = computePlatforms(submission.config);
  const platformProduct = findPlatformProduct(catalog, submission.config.floor);

  for (const platform of platforms) {
    const price = platformProduct?.pricing.sellCad ?? 0;
    sections.push({
      label: platformProduct?.descriptionFr ?? `Plateforme ${platform.kind}`,
      ...(platformProduct?.sku !== undefined && { sku: platformProduct.sku }),
      qty: 1,
      unitPrice: price,
      total: price,
      taxable: true,
      kind: 'platform',
    });
    if (platformProduct?.parts) {
      for (const pl of platformProduct.parts) {
        partsList.push({ partSku: pl.partSku, quantity: pl.quantity, description: pl.description });
      }
    }
  }

  // ------------------------------------------------------------------
  // 2. Treads
  // ------------------------------------------------------------------
  let totalRunIn = 0;

  if (submission.config.shape.kind === 'straight') {
    totalRunIn = submission.config.shape.totalRunIn;
    const treads = optimizeTreadSections(totalRunIn);

    for (const t of treads) {
      const product = findTreadProduct(catalog, t.lengthIn, submission.config.floor);
      const price = product?.pricing.sellCad ?? 0;
      sections.push({
        label: product?.descriptionFr ?? `Trottoir ${t.lengthIn}"`,
        ...(product?.sku !== undefined && { sku: product.sku }),
        qty: t.qty,
        unitPrice: price,
        total: price * t.qty,
        taxable: true,
        kind: 'tread',
      });
    }
  } else if (submission.config.shape.kind === 'L90') {
    totalRunIn = submission.config.shape.arm1RunIn + submission.config.shape.arm2RunIn;
    const arm1Treads = optimizeTreadSections(submission.config.shape.arm1RunIn);
    const arm2Treads = optimizeTreadSections(submission.config.shape.arm2RunIn);

    for (const t of [...arm1Treads, ...arm2Treads]) {
      const product = findTreadProduct(catalog, t.lengthIn, submission.config.floor);
      const price = product?.pricing.sellCad ?? 0;
      sections.push({
        label: product?.descriptionFr ?? `Trottoir ${t.lengthIn}"`,
        ...(product?.sku !== undefined && { sku: product.sku }),
        qty: t.qty,
        unitPrice: price,
        total: price * t.qty,
        taxable: true,
        kind: 'tread',
      });
    }
  } else if (submission.config.shape.kind === 'U180') {
    totalRunIn =
      submission.config.shape.arm1RunIn +
      submission.config.shape.arm2RunIn +
      submission.config.shape.landingDepthIn;
    const arm1Treads = optimizeTreadSections(submission.config.shape.arm1RunIn);
    const arm2Treads = optimizeTreadSections(submission.config.shape.arm2RunIn);

    for (const t of [...arm1Treads, ...arm2Treads]) {
      const product = findTreadProduct(catalog, t.lengthIn, submission.config.floor);
      const price = product?.pricing.sellCad ?? 0;
      sections.push({
        label: product?.descriptionFr ?? `Trottoir ${t.lengthIn}"`,
        ...(product?.sku !== undefined && { sku: product.sku }),
        qty: t.qty,
        unitPrice: price,
        total: price * t.qty,
        taxable: true,
        kind: 'tread',
      });
    }
  }
  // freeform / switchback: no auto tread — manual lines expected

  // ------------------------------------------------------------------
  // 3. Rails (both sides of each tread section)
  // ------------------------------------------------------------------
  if (submission.config.railType !== 'aucun' && totalRunIn > 0) {
    let runSegments: number[] = [];

    if (submission.config.shape.kind === 'straight') {
      runSegments = [totalRunIn];
    } else if (submission.config.shape.kind === 'L90') {
      runSegments = [submission.config.shape.arm1RunIn, submission.config.shape.arm2RunIn];
    } else if (submission.config.shape.kind === 'U180') {
      runSegments = [submission.config.shape.arm1RunIn, submission.config.shape.arm2RunIn];
    }

    for (const segRun of runSegments) {
      const treads = optimizeTreadSections(segRun);
      for (const t of treads) {
        const rail = findRailProduct(catalog, submission.config.railType, t.lengthIn);
        const price = rail?.pricing.sellCad ?? 0;
        const totalQty = t.qty * 2; // both sides
        sections.push({
          label: rail?.descriptionFr ?? `Garde-corps ${submission.config.railType} ${t.lengthIn}"`,
          ...(rail?.sku !== undefined && { sku: rail.sku }),
          qty: totalQty,
          unitPrice: price,
          total: price * totalQty,
          taxable: true,
          kind: 'rail',
        });
      }
    }
  }

  // ------------------------------------------------------------------
  // 4. Installation + Transport
  // ------------------------------------------------------------------
  if (submission.options.installation.enabled) {
    // Compute ramp surface in sq ft (approximate: width x total run)
    const widthFt = submission.config.widthIn / 12;
    const runFt = totalRunIn / 12;
    const sqft = widthFt * runFt;

    const installFee = computeInstallation(
      sqft,
      submission.config.widthIn,
      catalog.priceRules.installationPerSqFt,
    );
    sections.push({
      label: `Installation (${sqft.toFixed(1)} pi²)`,
      qty: 1,
      unitPrice: installFee,
      total: installFee,
      taxable: true,
      kind: 'install',
    });

    const transFee = computeTransport(
      submission.options.installation.transportKm,
      submission.options.uninstallation.enabled,
      catalog.priceRules.transportPerKm,
    );
    if (transFee > 0) {
      sections.push({
        label: `Transport ${submission.options.installation.transportKm} km${submission.options.uninstallation.enabled ? ' (aller-retour)' : ''}`,
        qty: 1,
        unitPrice: transFee,
        total: transFee,
        taxable: true,
        kind: 'transport',
      });
    }

    // Uninstallation labor fee — separate line, only when enabled
    if (submission.options.uninstallation.enabled) {
      const uninstallFee = computeUninstallation(
        sqft,
        submission.config.widthIn,
        catalog.priceRules.uninstallationPerSqFt,
      );
      sections.push({
        label: `Désinstallation (${sqft.toFixed(1)} pi²)`,
        qty: 1,
        unitPrice: uninstallFee,
        total: uninstallFee,
        taxable: true,
        kind: 'uninstall',
      });
    }
  }

  // ------------------------------------------------------------------
  // 5. Accessories (taxable per product.taxable flag)
  // ------------------------------------------------------------------
  for (const acc of submission.options.accessories) {
    const product = catalog.products.get(acc.sku);
    const price = product?.pricing.sellCad ?? 0;
    sections.push({
      label: product?.descriptionFr ?? String(acc.sku),
      sku: acc.sku,
      qty: acc.qty,
      unitPrice: price,
      total: price * acc.qty,
      taxable: product?.taxable ?? true,
      kind: 'accessory',
    });
  }

  // ------------------------------------------------------------------
  // 6. Passe-seuils (non-taxable)
  // ------------------------------------------------------------------
  for (const ps of submission.options.passeSeuils) {
    const product = catalog.products.get(ps.sku);
    const price = product?.pricing.sellCad ?? 0;
    sections.push({
      label: product?.descriptionFr ?? String(ps.sku),
      sku: ps.sku,
      qty: ps.qty,
      unitPrice: price,
      total: price * ps.qty,
      taxable: product?.taxable ?? false,
      kind: 'passe-seuil',
    });
  }

  // ------------------------------------------------------------------
  // 7. Manual lines
  // ------------------------------------------------------------------
  for (const ml of submission.manualLines) {
    sections.push({
      label: ml.description,
      qty: ml.qty,
      unitPrice: ml.unitPrice,
      total: ml.qty * ml.unitPrice,
      taxable: true,
      kind: 'manual',
    });
  }

  // ------------------------------------------------------------------
  // 8. Taxes
  // ------------------------------------------------------------------
  const totals = computeTaxes(sections);

  // ------------------------------------------------------------------
  // 8b. Phase A enrichment — weight + geometry summary on totals
  // ------------------------------------------------------------------
  // Total weight: sum of weightLbs × quantity for each resolved part.
  let totalWeightLbs = 0;
  for (const line of partsList) {
    const product = catalog.products.get(line.partSku);
    if (product?.weightLbs != null) {
      totalWeightLbs += product.weightLbs * line.quantity;
    }
  }
  totals.totalWeightLbs = totalWeightLbs;
  totals.totalWeightKg = totalWeightLbs * 0.453592;

  // Geometry totals (slope, length, shape label).
  const inclinedRunIn = computeInclinedRunIn(submission.config.shape);
  const heightIn = submission.config.heightIn;
  totals.runMeters = inclinedRunIn * 0.0254;
  if (inclinedRunIn > 0 && heightIn > 0) {
    totals.slopeRatio = inclinedRunIn / heightIn;
    totals.slopePercent = (heightIn / inclinedRunIn) * 100;
  }
  totals.shapeLabel = formatShapeLabel(submission.config.shape);

  // ------------------------------------------------------------------
  // 9. Rental (optional — does not affect taxed subtotal)
  // ------------------------------------------------------------------
  if (submission.options.rental.enabled && submission.options.rental.durationMonths > 0) {
    const rentableBase = sections
      .filter((s) => s.kind !== 'install' && s.kind !== 'uninstall' && s.kind !== 'transport')
      .reduce((a, s) => a + s.total, 0);
    const r = computeRental(rentableBase, submission.options.rental.durationMonths);
    totals.rental = { ...r, durationMonths: submission.options.rental.durationMonths };
  }

  // ------------------------------------------------------------------
  // 11. Rules-engine validation
  // ------------------------------------------------------------------
  const validation = validate(submission, catalog);
  for (const w of validation.warnings) {
    warnings.push({ ruleId: w.ruleId, severity: 'warn', humanMessage: w.humanMessage });
  }
  for (const e of validation.errors) {
    errors.push({ ruleId: e.ruleId, humanMessage: e.humanMessage });
  }
  for (const i of validation.infos) {
    warnings.push({ ruleId: i.ruleId, severity: 'info', humanMessage: i.humanMessage });
  }

  return { sections, totals, partsList, warnings, errors };
}
